import {useEffect, useLayoutEffect, useRef, useState} from 'react';

const Y_AXIS_W    = 60;
const X_LABEL_H   = 24;
const PAD_TOP     = 12;
const PAD_BTM     = 8;
const MONTH_GAP   = 10; // matches bar chart MONTH_GAP — keeps slot widths in sync
const MIN_POINT_W = 30; // HIT_R(10)*2 + MONTH_GAP — same density floor as bar chart
const PAD_RIGHT   = 10;
const MIN_LABEL_W = 34;

function labelStep(pointW) {
    const raw = Math.ceil(MIN_LABEL_W * 1.2 / pointW);
    return [1, 2, 3, 6, 12].find(s => s >= raw) ?? 12;
}
const DOT_R = 4;
const HIT_R = 10;
const TIP_W = 280;
const TIP_H = 130;

function fmtAxis(v) {
    if (Math.abs(v) >= 1_000_000) return `${+(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000)     return `${Math.round(v / 1_000)}k`;
    return `${Math.round(v)}`;
}

function lineTicks(min, max) {
    if (min === max) return [min - 1, min, min + 1];
    const rough = (max - min) / 4;
    const mag   = Math.pow(10, Math.floor(Math.log10(Math.abs(rough) || 1)));
    const step  = [1, 2, 2.5, 5, 10].map(c => c * mag).find(s => s >= rough) ?? mag * 10;
    const start = Math.floor(min / step) * step;
    const ticks = [start];
    // while loop guarantees last tick always >= max
    while (ticks[ticks.length - 1] < max && ticks.length <= 9)
        ticks.push(Math.round((ticks[ticks.length - 1] + step) * 1e9) / 1e9);
    // ensure first tick covers min
    while (ticks[0] > min)
        ticks.unshift(Math.round((ticks[0] - step) * 1e9) / 1e9);
    return ticks;
}

function ChartTooltip({tip}) {
    if (!tip?.text) return null;

    const vw = window.innerWidth, vh = window.innerHeight;

    let style;

    const top = tip.y + 8 + TIP_H > vh ? Math.max(4, tip.y - TIP_H - 8) : tip.y + 8;

    if (tip.x > vw / 2) {
        style = {top, right: window.innerWidth - tip.x + 8}
    } else {
        style = {top, left: tip.x + 8}
    }

    console.log(style)

    return (
        <div className="tchart-tooltip" style={style}>
            {tip.month && <div className="tchart-tooltip__month">{tip.month}</div>}
            {tip.text.split('\n').map((line, i) => {
                if (!line) return null;
                if (line.endsWith(':'))        return <div key={i} className="tchart-tooltip__label">{line.slice(0, -1)}</div>;
                if (line.startsWith('Total:')) return <div key={i} className="tchart-tooltip__total">{line}</div>;
                return <div key={i} className="tchart-tooltip__value">{line}</div>;
            })}
        </div>
    );
}

const TimelineLineChart = ({data, label, height = 220}) => {
    const scrollRef = useRef(null);
    const [scrollW, setScrollW] = useState(0);
    const [tooltip, setTooltip] = useState(null);
    const [hoveredIdx, setHoveredIdx] = useState(null);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el) setScrollW(Math.floor(el.getBoundingClientRect().width));
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => setScrollW(Math.floor(entries[0].contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el) el.scrollLeft = el.scrollWidth;
        });
        return () => cancelAnimationFrame(id);
    }, [data]);

    const rows   = data.slice(1);
    const labels = rows.map(r => r[0]);
    const values = rows.map(r => Number(r[1]) || 0);
    const tips   = rows.map(r => r[2] ?? '');

    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const vPad   = (rawMax - rawMin) * 0.1 || Math.abs(rawMax) * 0.1 || 1;
    // When all values are non-negative, don't let the lower bound go below 0
    const minBound = rawMin >= 0 ? Math.max(0, rawMin - vPad) : rawMin - vPad;
    const ticks  = lineTicks(minBound, rawMax + vPad);
    const yMin   = ticks[0];
    const yMax   = ticks[ticks.length - 1];
    const range  = yMax - yMin || 1;

    const SVG_H    = height + X_LABEL_H;
    const drawH    = height - PAD_TOP - PAD_BTM;
    const dataMinW = rows.length * MIN_POINT_W + PAD_RIGHT;
    const svgW     = Math.max(scrollW, dataMinW);
    const pointW   = rows.length > 0 ? (svgW - PAD_RIGHT) / rows.length : MIN_POINT_W;
    const pointSlot = pointW - MONTH_GAP; // active zone mirrors bar chart's barSlot
    const lblStep  = labelStep(pointW);

    const yScale = v => PAD_TOP + (1 - (v - yMin) / range) * drawH;
    // Centre the dot inside pointSlot, offset by MONTH_GAP/2 — identical to bar chart centring
    const xOfIdx = i => i * pointW + MONTH_GAP / 2 + pointSlot / 2;

    const pts = rows.map((_, i) => ({x: xOfIdx(i), y: yScale(values[i])}));
    const linePts = pts.map(p => `${p.x},${p.y}`).join(' ');

    const chartBaseY = height - PAD_BTM;
    const areaPath = pts.length
        ? [`M ${pts[0].x} ${chartBaseY}`,
           ...pts.map(p => `L ${p.x} ${p.y}`),
           `L ${pts[pts.length - 1].x} ${chartBaseY}`, 'Z'].join(' ')
        : '';

    const zeroY = yMin < 0 && yMax > 0 ? yScale(0) : null;

    const onHover = (e, text, i) => {
        setHoveredIdx(i);
        text && setTooltip({text, month: labels[i], x: e.clientX, y: e.clientY});
    };
    const onMove  = e => setTooltip(t => t ? {...t, x: e.clientX, y: e.clientY} : null);
    const onLeave = () => { setHoveredIdx(null); setTooltip(null); };

    return (
        <div className="timeline-chart-section">
            <span className="timeline-chart-section__label">{label}</span>
            <div className="tchart-body">
                <svg width={Y_AXIS_W} height={SVG_H} className="tchart-yaxis">
                    {ticks.map(v => (
                        <text key={v} x={Y_AXIS_W - 6} y={yScale(v) + 4}
                              textAnchor="end" fontSize={10} fill="var(--text-muted)" fontFamily="inherit">
                            {fmtAxis(v)}
                        </text>
                    ))}
                </svg>
                <div className="timeline-chart-scroll" ref={scrollRef}>
                    <svg width={svgW} height={SVG_H} className="tchart-svg">
                        {ticks.slice(0, -1).map((v, i) => (
                            <line key={`m${i}`} x1={0} y1={yScale((v + ticks[i + 1]) / 2)} x2={svgW} y2={yScale((v + ticks[i + 1]) / 2)}
                                  stroke="var(--border)" strokeWidth={1} strokeOpacity={0.35}/>
                        ))}
                        {ticks.map(v => (
                            <line key={v} x1={0} y1={yScale(v)} x2={svgW} y2={yScale(v)}
                                  stroke="var(--border)" strokeWidth={1}/>
                        ))}
                        {zeroY != null && (
                            <line x1={0} y1={zeroY} x2={svgW} y2={zeroY}
                                  stroke="var(--border)" strokeWidth={1.5}/>
                        )}
                        {areaPath && (
                            <path d={areaPath} fill="var(--primary)" fillOpacity={0.08} stroke="none"/>
                        )}
                        <polyline points={linePts} fill="none"
                                  stroke="var(--primary)" strokeWidth={2}
                                  strokeLinejoin="round" strokeLinecap="round"/>
                        {pts.map((p, i) => (
                            <g key={i}>
                                <circle cx={p.x} cy={p.y} r={DOT_R} className="tchart-dot"
                                        fill={hoveredIdx === i ? 'var(--primary)' : 'var(--surface)'}
                                        stroke="var(--primary)"
                                        strokeWidth={hoveredIdx === i ? 0 : 2}/>
                                <circle cx={p.x} cy={p.y} r={HIT_R} fill="transparent"
                                        className="tchart-hit"
                                        onMouseEnter={e => onHover(e, tips[i], i)}
                                        onMouseMove={onMove} onMouseLeave={onLeave}/>
                                {i % lblStep === 0 && (
                                    <text x={p.x} y={SVG_H - 6} textAnchor="middle"
                                          fontSize={10} fill="var(--text-muted)" fontFamily="inherit">
                                        {labels[i]}
                                    </text>
                                )}
                            </g>
                        ))}
                    </svg>
                </div>
            </div>
            <ChartTooltip tip={tooltip}/>
        </div>
    );
};

export default TimelineLineChart;
